// src/redux/offersSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createOffer as createOfferService,
  listOffers as listOffersService,
  getOffer as getOfferService,
  patchOffer as patchOfferService,
  deleteOffer as deleteOfferService,
} from "../services/offersService";

/**
 * Extract best possible API error message/details from many common shapes.
 * Supports:
 * - throw "string"
 * - throw Error(message)
 * - throw { response: { data: { error, message, details } } }
 * - throw { data: ... } / { body: ... }
 */
const extractError = (err, fallbackMessage) => {
  if (!err) return { code: "UNKNOWN", message: fallbackMessage, details: null };

  if (typeof err === "string") {
    return { code: "ERROR", message: err, details: null };
  }

  const data = err?.response?.data ?? err?.data ?? err?.body ?? null;

  if (data && typeof data === "object") {
    const message =
      data.message ||
      data.error ||
      data.title ||
      data.detail ||
      err?.message ||
      fallbackMessage;

    return {
      code: data.code || "API_ERROR",
      message: String(message),
      details: data.details ?? data.errors ?? null,
    };
  }

  return {
    code: "ERROR",
    message: err?.message || fallbackMessage,
    details: null,
  };
};

const isDuplicateCodeError = (e) => {
  const { message, details } = extractError(e, "");
  const hay = `${message} ${JSON.stringify(details || {})}`.toLowerCase();
  return (
    hay.includes("already exists") ||
    hay.includes("duplicate") ||
    hay.includes("conditionalcheckfailed") ||
    hay.includes("conditional check failed") ||
    hay.includes("code exists")
  );
};

const initialState = {
  items: [],
  lastKey: null,
  hasMore: true,

  // ✅ split list loading states to avoid flicker
  loading: false,        // "initial load" (only when list empty)
  refreshing: false,     // "refetch while keeping table visible"
  loadingMore: false,    // pagination / load more
  error: null,

  creating: false,
  createError: null,

  fetchingOne: false,
  fetchOneError: null,
  current: null,

  updating: false,
  updateError: null,

  deleting: false,
  deleteError: null,
};

const getOfferId = (o) => {
  const id = o?.offerId ?? o?.id ?? o?._id ?? null;
  return id == null ? null : String(id);
};

const dedupeById = (items, getId) => {
  const seen = new Set();
  const out = [];
  for (const item of items || []) {
    const id = getId(item);
    const key = id != null ? String(id) : JSON.stringify(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
};

const parseListResponse = (resp) => {
  if (Array.isArray(resp)) return { items: resp, lastKey: null };

  if (Array.isArray(resp?.items)) {
    return {
      items: resp.items,
      lastKey:
        resp.last_key ??
        resp.lastKey ??
        resp.next_cursor ??
        resp.nextCursor ??
        null,
    };
  }

  return { items: [], lastKey: null };
};

/**
 * listOffersAsync({ params?: {...}, append?: boolean })
 * params supports: limit, last_key, active, campaignId, q
 */
export const listOffersAsync = createAsyncThunk(
  "offers/list",
  async ({ params = {}, append = false } = {}, { rejectWithValue }) => {
    try {
      const resp = await listOffersService(params);
      const parsed = parseListResponse(resp);
      return { ...parsed, append };
    } catch (e) {
      return rejectWithValue(extractError(e, "Failed to load offers."));
    }
  }
);

export const createOfferAsync = createAsyncThunk(
  "offers/create",
  async (payload, { rejectWithValue }) => {
    try {
      const created = await createOfferService(payload);
      return created || null;
    } catch (e) {
      if (isDuplicateCodeError(e)) {
        return rejectWithValue({
          code: "DUPLICATE_CODE",
          message: "That offer code is already in use. Please generate a new one.",
          details: extractError(e, "").details,
        });
      }
      return rejectWithValue(extractError(e, "Failed to create offer."));
    }
  }
);

export const getOfferAsync = createAsyncThunk(
  "offers/getOne",
  async (arg, { rejectWithValue }) => {
    try {
      const offerId = typeof arg === "object" ? arg?.offerId : arg;
      if (!offerId) throw new Error("offerId is required.");
      const offer = await getOfferService(offerId);
      return offer || null;
    } catch (e) {
      return rejectWithValue(extractError(e, "Failed to load offer."));
    }
  }
);

export const patchOfferAsync = createAsyncThunk(
  "offers/patch",
  async ({ offerId, payload }, { rejectWithValue }) => {
    try {
      if (!offerId) throw new Error("offerId is required.");
      if (!payload || typeof payload !== "object") {
        throw new Error("Valid payload is required.");
      }
      const updated = await patchOfferService(offerId, payload);
      return updated || null;
    } catch (e) {
      return rejectWithValue(extractError(e, "Failed to update offer."));
    }
  }
);

export const deleteOfferAsync = createAsyncThunk(
  "offers/delete",
  async (offerId, { rejectWithValue }) => {
    try {
      if (!offerId) throw new Error("offerId is required.");
      const resp = await deleteOfferService(offerId);
      return { offerId: String(offerId), resp: resp || { ok: true } };
    } catch (e) {
      return rejectWithValue(extractError(e, "Failed to delete offer."));
    }
  }
);

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    clearOffersErrors(state) {
      state.error = null;
      state.createError = null;
      state.fetchOneError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    resetOffersState() {
      return { ...initialState };
    },
    clearCurrentOffer(state) {
      state.current = null;
      state.fetchingOne = false;
      state.fetchOneError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // LIST
      .addCase(listOffersAsync.pending, (state, action) => {
        const append = !!action.meta?.arg?.append;
        state.error = null;

        // ✅ DO NOT clear items here. We keep UI stable.
        const hasData = Array.isArray(state.items) && state.items.length > 0;

        if (append) {
          state.loadingMore = true;
          state.loading = false;
          state.refreshing = false;
          return;
        }

        // non-append fetch:
        // if we already have data, treat as refresh (keep table visible)
        if (hasData) {
          state.refreshing = true;
          state.loading = false;
          state.loadingMore = false;
          return;
        }

        // first load (empty list)
        state.loading = true;
        state.refreshing = false;
        state.loadingMore = false;
      })
      .addCase(listOffersAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.loadingMore = false;

        const { items, lastKey, append } = action.payload || {
          items: [],
          lastKey: null,
          append: false,
        };

        const merged = append ? [...state.items, ...(items || [])] : items || [];
        state.items = dedupeById(merged, getOfferId);

        state.lastKey = lastKey || null;
        state.hasMore = Boolean(lastKey);
      })
      .addCase(listOffersAsync.rejected, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.loadingMore = false;
        state.error = action.payload || { code: "LIST_FAILED", message: "Failed to load offers." };
      })

      // CREATE
      .addCase(createOfferAsync.pending, (state) => {
        state.creating = true;
        state.createError = null;
      })
      .addCase(createOfferAsync.fulfilled, (state, action) => {
        state.creating = false;
        const created = action.payload;
        if (!created) return;

        const id = getOfferId(created);
        if (id) state.items = state.items.filter((o) => getOfferId(o) !== id);

        state.items = [created, ...state.items];
      })
      .addCase(createOfferAsync.rejected, (state, action) => {
        state.creating = false;
        state.createError =
          action.payload || { code: "CREATE_FAILED", message: "Failed to create offer." };
      })

      // GET ONE
      .addCase(getOfferAsync.pending, (state) => {
        state.fetchingOne = true;
        state.fetchOneError = null;
      })
      .addCase(getOfferAsync.fulfilled, (state, action) => {
        state.fetchingOne = false;
        const offer = action.payload;
        state.current = offer || null;

        if (offer) {
          const id = getOfferId(offer);
          if (id) {
            state.items = [offer, ...state.items.filter((o) => getOfferId(o) !== id)];
          }
        }
      })
      .addCase(getOfferAsync.rejected, (state, action) => {
        state.fetchingOne = false;
        state.fetchOneError =
          action.payload || { code: "GET_FAILED", message: "Failed to load offer." };
      })

      // PATCH
      .addCase(patchOfferAsync.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(patchOfferAsync.fulfilled, (state, action) => {
        state.updating = false;
        const updated = action.payload;
        if (!updated) return;

        const id = getOfferId(updated);
        if (id) {
          state.items = state.items.map((o) => (getOfferId(o) === id ? updated : o));
          if (getOfferId(state.current) === id) state.current = updated;
        }
      })
      .addCase(patchOfferAsync.rejected, (state, action) => {
        state.updating = false;
        state.updateError =
          action.payload || { code: "PATCH_FAILED", message: "Failed to update offer." };
      })

      // DELETE
      .addCase(deleteOfferAsync.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteOfferAsync.fulfilled, (state, action) => {
        state.deleting = false;
        const offerId = action.payload?.offerId;
        const resp = action.payload?.resp;

        if (!offerId) return;

        // If backend returns an updated offer (soft delete), prefer replacing.
        if (resp && typeof resp === "object" && getOfferId(resp)) {
          const id = getOfferId(resp);
          state.items = state.items.map((o) => (getOfferId(o) === id ? resp : o));
          if (getOfferId(state.current) === id) state.current = resp;
          return;
        }

        // Hard delete: remove from store
        state.items = state.items.filter((o) => getOfferId(o) !== String(offerId));
        if (getOfferId(state.current) === String(offerId)) state.current = null;
      })
      .addCase(deleteOfferAsync.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError =
          action.payload || { code: "DELETE_FAILED", message: "Failed to delete offer." };
      });
  },
});

export const { clearOffersErrors, resetOffersState, clearCurrentOffer } = offersSlice.actions;

// selectors
export const selectOffers = (state) => state.offers?.items || [];

export const selectOffersLoading = (state) => state.offers?.loading || false;         // initial only
export const selectOffersRefreshing = (state) => state.offers?.refreshing || false;   // keep table visible
export const selectOffersLoadingMore = (state) => state.offers?.loadingMore || false; // pagination

// convenience selector: any list network activity
export const selectOffersBusy = (state) =>
  Boolean(selectOffersLoading(state) || selectOffersRefreshing(state) || selectOffersLoadingMore(state));

export const selectOffersError = (state) => state.offers?.error || null;

export const selectOffersLastKey = (state) => state.offers?.lastKey || null;
export const selectOffersHasMore = (state) => state.offers?.hasMore || false;

export const selectOfferCreating = (state) => state.offers?.creating || false;
export const selectOfferCreateError = (state) => state.offers?.createError || null;

export const selectCurrentOffer = (state) => state.offers?.current || null;
export const selectCurrentOfferLoading = (state) => state.offers?.fetchingOne || false;
export const selectCurrentOfferError = (state) => state.offers?.fetchOneError || null;

export const selectOfferUpdating = (state) => state.offers?.updating || false;
export const selectOfferUpdateError = (state) => state.offers?.updateError || null;

export const selectOfferDeleting = (state) => state.offers?.deleting || false;
export const selectOfferDeleteError = (state) => state.offers?.deleteError || null;

export default offersSlice.reducer;
